<template>
  <div id="main-content" class="feature-landing-page">
    <Alert v-if="!isCRM && ((navState?.refData && navState.refData.length === 0) || getRefDataFailed)" alertType="error" />

    <div v-else>
      <SuccessBanner :header="'Your claim submission was successful'" class="banner">
        <div v-html="claimSubmissionInformationText"></div>
      </SuccessBanner>
      <Alert style="margin-top: 1.5rem">
        <p v-html="this.claimSubmissionKeepPrivateAlertText"></p>
      </Alert>
    </div>

    <section class="top-content fr-view">
      <div id="main-content">
        <p v-html="this.claimSubmissionKeepPrivateInformationText" style="margin-top: 36px" />
      </div>
    </section>
    <section class="mid-content fr-view">
      <table class="borderless">
        <h3 class="h6 sub-heading-underlined">Claim Overview</h3>
        <tbody>
          <tr>
            <th>Member receiving services</th>
          </tr>
          <tr>
            <td>{{ member }}</td>
          </tr>
          <tr>
            <th>Payable to</th>
          </tr>
          <tr>
            <td>
              <p v-for="(item, i) in payableTo" :key="`payable-to-data-${i}`">{{ item }}</p>
            </td>
          </tr>
          <tr>
            <th>Claim type</th>
          </tr>
          <tr>
            <td>{{ claimType }}</td>
          </tr>
          <tr>
            <th>Provider</th>
          </tr>
          <tr>
            <td>
              <p>{{ providerNumber }}</p>
              <p>{{ providerName }}</p>
              <p>{{ titleCase(providerAddressLine1) }}</p>
              <p>{{ titleCase(providerAddressLine2) }}</p>
              <p>{{ titleCase(providerAddressLine3) }}</p>
              <p>{{ providerAddressLocality }}</p>
            </td>
          </tr>
          <tr>
            <th>Date claim was submitted</th>
          </tr>
          <tr>
            <td>{{ reverseFormatFormDate(submitted) }}</td>
          </tr>
        </tbody>
      </table>
    </section>
    <div class="button-wrapper">
      <router-link :to="{ name: CLAIMS_MAKE_A_CLAIM }" class="button primary">Make another claim</router-link>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { VUEX_BENEFIT_QUOTES_CLAIMS, VUEX_GENERAL } from '@/constants/VuexConstants';
import { Alert, SuccessBanner } from '@/components';
import { getAddressLocalityText, titleCase, isBuildCRM } from '@/utils';
import { reverseFormatFormDate } from '@general/js/utils';
import { CLAIMS_MAKE_A_CLAIM, ERROR_404_PAGE_NOT_FOUND } from '@/router/routeNames';

const isCRM = isBuildCRM();

export default {
  name: 'ClaimResultSubmissionKeepPrivate',

  components: {
    Alert,
    SuccessBanner,
  },

  data() {
    return {
      isCRM,
      CLAIMS_MAKE_A_CLAIM,
      member: '',
      payableTo: [],
      claimType: '',
      providerNumber: '',
      providerName: '',
      providerAddressLine1: '',
      providerAddressLine2: '',
      providerAddressLine3: '',
      providerAddressLocality: '',
      relatedArticles: [],
      submitted: '',
      claimSubmissionInformationText: '',
      claimSubmissionKeepPrivateAlertText: '',
      claimSubmissionKeepPrivateInformationText: '',
      getRefDataFailed: false,
      navState: window.history.state || {},
    };
  },

  computed: {
    ...mapGetters({
      policyNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      currentIsExtrasFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyIsExtrasFlag,
    }),
  },

  mounted() {
    if (isCRM) {
      this.resetVuex();
    }
  },

  beforeMount() {
    if (!this.navState.formData && !this.navState.refData) {
      this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
    } else {
      // if is not CRM and current policy's `isExtras` flag is false, redirect to member portal homepage
      if (!this.isCRM && !this.currentIsExtrasFlag) {
        this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
      }

      /**
       * only fetch if is not CRM and `isExtras` flag is true or is CRM build
       */
      if ((!this.isCRM && this.currentIsExtrasFlag) || this.isCRM) {
        this.fetchData();
      }
    }
  },

  methods: {
    ...mapMutations('BenefitQuotesClaims', {
      resetVuex: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.resetState,
    }),

    fetchData() {
      const { formData, refData } = !this.isCRM ? this.navState : this.$parent.routeParams;

      if (refData === undefined) {
        this.getRefDataFailed = true;
      } else {
        this.claimSubmissionInformationText = refData.claimSubmissionInformationText.informationText;
        this.claimSubmissionKeepPrivateAlertText = refData.claimSubmissionKeepPrivateAlertText.informationText;
        this.claimSubmissionKeepPrivateInformationText = refData.claimSubmissionKeepPrivateInformationText.informationText;
      }

      this.member = titleCase(formData.memberFullName);
      this.claimType = titleCase(formData.modality?.claimTypeName);
      this.payableTo = formData.payableTo;
      this.providerName = `${formData.provider.firstName} ${formData.provider.lastName}`;
      this.providerAddressLine1 = formData.provider.addressLine1 ?? '';
      this.providerAddressLine2 = formData.provider.addressLine2 ?? '';
      this.providerAddressLine3 = formData.provider.addressLine3 ?? '';
      this.providerAddressLocality = getAddressLocalityText(formData.provider.suburb ?? '', formData.provider.state ?? '', formData.provider.postcode ?? '');
      this.submitted = formData.claimDate;
    },

    titleCase,
    reverseFormatFormDate,
  },
};
</script>

<style lang="scss" scoped>
h1 {
  margin-left: 0;
  margin-right: 0;
  float: none;
}
.top-content {
  margin-bottom: 0;

  #main-content {
    padding: 0;

    @include mobile {
      margin-bottom: 0 !important;
    }
  }

  & > * {
    margin-bottom: 0;
  }
}
.sub-heading-underlined {
  margin-bottom: 0.75rem;
}

.mid-content {
  --NUMBER-OF-COLUMS: 2;
  display: grid;
  grid-template-columns: repeat(var(--NUMBER-OF-COLUMS), 1fr);
  margin-bottom: 30px;

  @include mobile {
    --NUMBER-OF-COLUMS: 1;
  }

  .h6 {
    margin: 1rem 1rem 0.75rem 1rem;
  }

  .button-wrapper {
    margin: 20px 0;
  }

  table {
    display: table;
    width: 75%;
    tbody {
      tr {
        th,
        td {
          text-align: left;
          padding-top: 0;

          p {
            margin-bottom: 0;
          }
        }

        th {
          padding-bottom: 0;
        }
      }
    }
  }
}
</style>
